<template>
  <div
    class="stats-card"
    :class="[{ 'stats-card--clickable': clickable, 'stats-card--loading': loading }]"
    @click="handleClick"
  >
    <div class="stats-card__label">{{ label }}</div>
    <div class="stats-card__content">
      <div class="stats-card__value">
        <template v-if="loading">
          <span class="stats-card__skeleton"></span>
        </template>
        <template v-else>
          {{ formattedValue }}
        </template>
      </div>
      <StatsTrend
        v-if="trend !== null && trend !== undefined && !loading"
        :value="trend"
        :suffix="trendSuffix"
        :inverted="trendInverted"
      />
    </div>
  </div>
</template>

<script>
  /**
   * StatsCard Component
   *
   * A reusable statistics card that displays a metric with optional trend indicator.
   * Supports multiple formats (number, currency, percentage).
   *
   * @example
   * <StatsCard
   *   label="Openstaand"
   *   :value="82500"
   *   format="currency"
   *   :trend="15.2"
   * />
   */
  import { defineComponent, computed, getCurrentInstance } from 'vue';
  import StatsTrend from './StatsTrend.vue';

  export default defineComponent({
    name: 'StatsCard',

    components: {
      StatsTrend,
    },

    props: {
      /**
       * Label displayed above the value
       */
      label: {
        type: String,
        required: true,
      },
      /**
       * The main value to display
       */
      value: {
        type: [Number, String],
        required: true,
      },
      /**
       * Value format type
       */
      format: {
        type: String,
        default: 'number',
        validator: (v) => ['number', 'currency', 'percentage'].includes(v),
      },
      /**
       * Currency code for currency format
       */
      currency: {
        type: String,
        default: 'EUR',
      },
      /**
       * Locale for number formatting
       */
      locale: {
        type: String,
        default: 'nl-NL',
      },
      /**
       * Trend percentage (positive or negative)
       */
      trend: {
        type: Number,
        default: null,
      },
      /**
       * Suffix for trend value
       */
      trendSuffix: {
        type: String,
        default: '%',
      },
      /**
       * Invert trend colors (positive = bad)
       */
      trendInverted: {
        type: Boolean,
        default: false,
      },
      /**
       * Whether the card is clickable
       */
      clickable: {
        type: Boolean,
        default: false,
      },
      /**
       * Loading state
       */
      loading: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['click'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();

      const formattedValue = computed(() => {
        if (props.value === null || props.value === undefined) {
          return '-';
        }

        const numValue = typeof props.value === 'string' ? parseFloat(props.value) : props.value;

        if (isNaN(numValue)) {
          return props.value;
        }

        switch (props.format) {
          case 'currency':
            return new Intl.NumberFormat(props.locale, {
              style: 'currency',
              currency: props.currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(numValue);

          case 'percentage':
            return new Intl.NumberFormat(props.locale, {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            }).format(numValue / 100);

          case 'number':
          default:
            return new Intl.NumberFormat(props.locale).format(numValue);
        }
      });

      const handleClick = (event) => {
        if (props.clickable) {
          emit('click', event);
        }
      };

      return {
        formattedValue,
        handleClick,
      };
    },
  });
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .stats-card {
    display: flex;
    flex-direction: column;
    padding: 1rem 1.25rem;
    background-color: var(--white);
    border: 1px solid var(--slate-200);
    border-radius: 12px;
    min-width: 140px;
    transition:
      box-shadow 0.15s ease,
      border-color 0.15s ease,
      transform 0.15s ease;
    @include lg-up {
      padding: 0.75rem 1rem;
    }

    &--clickable {
      cursor: pointer;

      @include hover {
        border-color: var(--slate-300);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
      }
    }

    &--loading {
      .stats-card__skeleton {
        display: inline-block;
        height: 28px;
        width: 60px;
        background: linear-gradient(
          90deg,
          var(--slate-100) 25%,
          var(--slate-200) 50%,
          var(--slate-100) 75%
        );
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        border-radius: 4px;
      }
    }

    &__label {
      font-size: 13px;
      font-weight: 500;
      color: var(--slate-500);
      white-space: nowrap;
      margin-bottom: 0.25px;
    }

    &__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    &__value {
      font-size: 1.125rem;
      font-weight: 700;
      line-height: 1.2;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
</style>
